/**
 * JotRx Encounter Action Hook
 *
 * Shared business logic for JotRx encounter CTAs.
 * Handles setup state, patient connection checks, DoseSpot SSO,
 * connect modal state, and patient demographics fetching.
 */

import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';
import { useSnackbar } from 'notistack';

import { useGetPatientPrescribingReadinessQuery } from '../store';
import { notifyDoseSpotReady } from '../utils/notifyDoseSpotReady';

import { useDoseSpotLinks } from './useDoseSpotLinks';
import { useJotRxSetupState } from './useJotRxSetupState';
import { useProxyOnBehalfOf } from './useProxyOnBehalfOf';

interface PatientAddressLike {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

interface PatientDemographics {
  first_name?: string;
  last_name?: string;
  dob?: string;
  sex?: string;
  gender?: string;
  mobile_phone?: string;
  phone?: string;
  primary_address?: PatientAddressLike | null;
  // Legacy flat address fields, kept as fallback
  address?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface PatientConnectData {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface UseJotRxEncounterActionOptions {
  patientId?: string | null;
  patientData?: PatientConnectData;
  source?: 'encounter_cta' | 'patient_detail' | 'medications_table';
}

export function useJotRxEncounterAction({
  patientId,
  patientData,
  source = 'encounter_cta',
}: UseJotRxEncounterActionOptions) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { jotrx } = useEventTracking();
  const { setupState, isJotRxEnabled, isJotRxPurchasable, isLoading, cta, isFullySetup } = useJotRxSetupState();
  const {
    data: prescribingReadiness,
    isLoading: isPatientStatusLoading,
    refetch: refetchReadiness,
  } = useGetPatientPrescribingReadinessQuery(
    { patientId: patientId || '' },
    { skip: !patientId || !isFullySetup },
  );
  const { getAccessToken } = useAccessToken();
  const { getPatientHref } = useDoseSpotLinks(source);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [patientDemographics, setPatientDemographics] = useState<PatientDemographics | null>(null);
  const [pendingOnBehalfOfProviderId, setPendingOnBehalfOfProviderId] = useState<string | undefined>();

  const { isProxy, prescribers } = useProxyOnBehalfOf();
  const [proxyMenuAnchor, setProxyMenuAnchor] = useState<null | HTMLElement>(null);
  const isPatientConnected = prescribingReadiness?.connected ?? false;

  // Proxies have no personal JotRx entitlement; the org being connected to
  // DoseSpot is what gates them (enforced by setupState + backend).
  const isVisible =
    (isJotRxEnabled || isJotRxPurchasable || isProxy) && !isLoading;

  // Fetch patient demographics for connect modal pre-fill
  useEffect(() => {
    if (!patientId || patientData) {
      setPatientDemographics(null);
      return;
    }
    let cancelled = false;
    const fetchPatientData = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${patientId}&origin=JotRxConnect`,
          accessToken,
        });
        if (response.ok && !cancelled) {
          const data = (await response.value) as { patient?: PatientDemographics } | undefined;
          setPatientDemographics(data?.patient || null);
        }
      } catch {
        // Non-critical — modal still works without pre-fill
      }
    };
    void fetchPatientData();
    return () => { cancelled = true; };
  }, [patientId, patientData, getAccessToken]);

  const canLaunch =
    !!patientId &&
    !!prescribingReadiness &&
    (prescribingReadiness.connected || prescribingReadiness.connectable_from_chart);
  const getPrescribeHref = (providerId?: string): string | undefined =>
    canLaunch ? getPatientHref(patientId, providerId) : undefined;
  const prescribeHref =
    isFullySetup && (!isProxy || prescribers.length === 1)
      ? getPrescribeHref(isProxy ? prescribers[0]?.userId : undefined)
      : undefined;
  const launchPrescribe = useCallback(
    (providerId?: string): void => {
      if (canLaunch) return; // The anchor performs navigation.
      setPendingOnBehalfOfProviderId(providerId);
      setShowConnectModal(true);
    },
    [canLaunch],
  );

  const handleClick = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      jotrx.setupStarted({ source, setup_state: setupState });

      if (setupState === 'fully_setup' && patientId) {
        if (isPatientStatusLoading || !prescribingReadiness) {
          enqueueSnackbar('Checking e-prescription readiness. Please try again in a moment.', {
            variant: 'info',
          });
          return;
        }
        if (isProxy) {
          // GDN-1119: proxy must act on behalf of a connected prescriber.
          if (prescribers.length === 0) {
            enqueueSnackbar(
              'A prescriber in your organization must finish DoseSpot setup ' +
                'before you can prepare prescriptions on their behalf.',
              { variant: 'warning' },
            );
            return;
          }
          if (prescribers.length === 1) {
            launchPrescribe(prescribers[0].userId);
          } else if (e) {
            setProxyMenuAnchor(e.currentTarget);
          }
          return;
        }
        launchPrescribe();
        return;
      }

      navigate(cta.route);
    },
    [
      jotrx,
      source,
      setupState,
      patientId,
      isPatientStatusLoading,
      prescribingReadiness,
      isProxy,
      prescribers,
      enqueueSnackbar,
      launchPrescribe,
      cta.route,
      navigate,
    ],
  );

  const handleConnectSuccess = useCallback(() => {
    void refetchReadiness();
    const onBehalfOfProviderId =
      pendingOnBehalfOfProviderId ||
      (isProxy && prescribers.length === 1 ? prescribers[0].userId : undefined);
    setPendingOnBehalfOfProviderId(undefined);

    if (isProxy && !onBehalfOfProviderId) {
      enqueueSnackbar(
        'A prescriber in your organization must finish DoseSpot setup ' +
          'before you can prepare prescriptions on their behalf.',
        { variant: 'warning' },
      );
      return;
    }

    if (patientId)
      notifyDoseSpotReady(enqueueSnackbar, getPatientHref(patientId, onBehalfOfProviderId));
  }, [
    refetchReadiness,
    pendingOnBehalfOfProviderId,
    isProxy,
    prescribers,
    enqueueSnackbar,
    patientId,
    getPatientHref,
  ]);

  const selectProxyPrescriber = useCallback(
    (userId: string) => {
      setProxyMenuAnchor(null);
      launchPrescribe(userId);
    },
    [launchPrescribe],
  );

  const fetchedConnectModalPatientData = patientDemographics
    ? {
        firstName: patientDemographics.first_name,
        lastName: patientDemographics.last_name,
        dob: patientDemographics.dob || undefined,
        gender: patientDemographics.sex || patientDemographics.gender,
        phone: getPatientPrimaryContactPhone(patientDemographics),
        address: patientDemographics.primary_address?.line1 || patientDemographics.address,
        address2: patientDemographics.primary_address?.line2 || patientDemographics.address_2,
        city: patientDemographics.primary_address?.city || patientDemographics.city,
        state: patientDemographics.primary_address?.state || patientDemographics.state,
        zipCode: patientDemographics.primary_address?.zip || patientDemographics.zip,
      }
    : undefined;
  const connectModalPatientData = patientData || fetchedConnectModalPatientData;

  return {
    setupState,
    isVisible,
    isFullySetup,
    isJotRxPurchasable,
    isPatientConnected,
    isPatientStatusLoading,
    isLoading,
    cta,
    handleClick,
    prescribeHref,
    getPrescribeHref,
    showConnectModal,
    setShowConnectModal,
    handleConnectSuccess,
    connectModalPatientData,
    // GDN-1119 proxy → prescriber picker (only when >1 connected prescriber)
    proxyPrescribers: prescribers,
    proxyMenuAnchor,
    closeProxyMenu: () => setProxyMenuAnchor(null),
    selectProxyPrescriber,
  };
}
